import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Collapse,
  Tooltip,
  Snackbar,
} from '@mui/material';
import {
  People as PeopleIcon,
  Business as BusinessIcon,
  Assignment as JobIcon,
  CheckCircle as CompletedIcon,
  Error as FailedIcon,
  HourglassEmpty as ProcessingIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';
import { adminApi, OverviewStats } from '../services/api';
import {
  CLOUDWATCH_REGION,
  LAMBDA_LOG_GROUP,
  getS3UploadsConsoleUrl,
  getS3OutputConsoleUrl,
  getS3OcrInputConsoleUrl,
  getS3AiResponseConsoleUrl,
} from '../config/aws-console';

const COLORS = ['#38a169', '#e53e3e', '#dd6b20', '#3182ce'];
const CLOUDWATCH_OBJECT_SEPARATOR = '$257E';
const CLOUDWATCH_ARRAY_START = '$2528';
const CLOUDWATCH_STRING_PREFIX = '$2527';
const CLOUDWATCH_OBJECT_END = '$2529';

enum CloudWatchTimeType {
  ABSOLUTE = 'ABSOLUTE',
}

enum CloudWatchTimeZone {
  LOCAL = 'Local',
}

const encodeCloudWatchString = (value: string): string =>
  value
    .split('')
    .map((character) =>
      /^[a-zA-Z0-9-.]$/.test(character)
        ? character
        : `*${character.charCodeAt(0).toString(16).padStart(2, '0')}`
    )
    .join('');

const encodeCloudWatchArray = (values: string[]): string =>
  [
    CLOUDWATCH_OBJECT_SEPARATOR,
    CLOUDWATCH_ARRAY_START,
    ...values.flatMap((value) => [
      CLOUDWATCH_OBJECT_SEPARATOR,
      CLOUDWATCH_STRING_PREFIX,
      encodeCloudWatchString(value),
    ]),
  ].join('');

const encodeCloudWatchObject = (
  payload: Record<string, string | number | boolean | string[]>,
  prefix: string[] = [CLOUDWATCH_OBJECT_SEPARATOR, CLOUDWATCH_ARRAY_START]
): string => {
  const output: string[] = [];

  Object.entries(payload).forEach(([key, value], index) => {
    if (index === 0) {
      output.push(...prefix, key);
    } else {
      output.push(CLOUDWATCH_OBJECT_SEPARATOR, key);
    }

    if (typeof value === 'boolean' || typeof value === 'number') {
      output.push(CLOUDWATCH_OBJECT_SEPARATOR, String(value));
      return;
    }

    if (Array.isArray(value)) {
      output.push(encodeCloudWatchArray(value));
      return;
    }

    output.push(
      CLOUDWATCH_OBJECT_SEPARATOR,
      CLOUDWATCH_STRING_PREFIX,
      encodeCloudWatchString(value)
    );
  });

  return output.join('');
};

const getCloudWatchLogsUrl = (jobId: string, createdAt: string): string => {
  const createdAtMs = new Date(createdAt).getTime();
  const start = new Date(createdAtMs - 5 * 60 * 1000).toISOString();
  const end = new Date(createdAtMs + 60 * 60 * 1000).toISOString();
  const editorString = [
    'fields @timestamp, @requestId, @message',
    `| filter @message like /${jobId}/`,
    '| sort @timestamp asc',
    '| limit 200',
  ].join('\n');

  const queryDetail = [
    encodeCloudWatchObject({
      editorString,
      start,
      end,
      timeType: CloudWatchTimeType.ABSOLUTE,
      tz: CloudWatchTimeZone.LOCAL,
      isLiveTail: false,
    }),
    encodeCloudWatchObject(
      {
        source: [LAMBDA_LOG_GROUP],
      },
      [CLOUDWATCH_OBJECT_SEPARATOR]
    ),
    CLOUDWATCH_OBJECT_END,
    CLOUDWATCH_OBJECT_END,
  ].join('');

  return `https://${CLOUDWATCH_REGION}.console.aws.amazon.com/cloudwatch/home?region=${CLOUDWATCH_REGION}#logsV2:logs-insights$3FqueryDetail$3D${queryDetail}`;
};

interface StatCardProps {
  title: string;
  value: number | string | null;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, subtitle }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={700} color={color}>
            {value ?? '-'}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            backgroundColor: `${color}15`,
            color: color,
          }}
        >
          {icon}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const hasLoadedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');

  // Helper function to format milliseconds to human-readable duration
  const formatDuration = (ms: number): string => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      const remainingSeconds = seconds % 60;
      return `${hours}h ${remainingMinutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${seconds}s`;
    }
  };

  useEffect(() => {
    const fetchStats = async () => {
      try {
        if (!hasLoadedRef.current) {
          setLoading(true);
        } else {
          setTableLoading(true);
        }
        const response = await adminApi.getOverviewStats({
          recentJobsOffset: page * rowsPerPage,
          recentJobsLimit: rowsPerPage
        });
        if (response.success) {
          setStats(response.data);
        } else {
          setError('Failed to load statistics');
        }
      } catch (err: any) {
        setError(err.response?.data?.message || err.message || 'Failed to load statistics');
      } finally {
        hasLoadedRef.current = true;
        setLoading(false);
        setTableLoading(false);
      }
    };

    fetchStats();
  }, [page, rowsPerPage]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const statusData = stats?.jobsByStatus ? [
    { name: 'Completed', value: stats.jobsByStatus.COMPLETED || 0 },
    { name: 'Failed', value: stats.jobsByStatus.FAILED || 0 },
    { name: 'Processing', value: stats.jobsByStatus.PROCESSING || 0 },
    { name: 'Queued', value: stats.jobsByStatus.QUEUED || 0 },
  ].filter(d => d.value > 0) : [];

  const jobTypeData = stats?.topJobTypes?.slice(0, 8) || [];

  const toggleRowExpansion = (jobId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(jobId)) {
        newSet.delete(jobId);
      } else {
        newSet.add(jobId);
      }
      return newSet;
    });
  };

  const copyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage(label);
      setSnackbarOpen(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Dashboard
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Overview for period:
          </Typography>
          <Chip 
            label={stats?.period || '-'} 
            size="small" 
            color="primary" 
            variant="outlined" 
          />
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Users"
            value={stats?.totalUsers ?? null}
            icon={<PeopleIcon />}
            color="#3182ce"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Organizations"
            value={stats?.totalOrganizations ?? null}
            icon={<BusinessIcon />}
            color="#805ad5"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Jobs Today"
            value={stats?.jobsToday ?? null}
            icon={<JobIcon />}
            color="#38a169"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Cost Today"
            value={stats?.totalCostToday !== undefined ? `$${stats.totalCostToday.toFixed(2)}` : null}
            icon={<JobIcon />}
            color="#e53e3e"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Currently Processing"
            value={stats?.activeProcessing ?? null}
            icon={<ProcessingIcon />}
            color="#dd6b20"
          />
        </Grid>
      </Grid>

      {/* Job Status Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CompletedIcon sx={{ color: 'success.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.COMPLETED || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Completed Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FailedIcon sx={{ color: 'error.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.FAILED || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Failed Today
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ProcessingIcon sx={{ color: 'warning.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.PROCESSING || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Processing
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <JobIcon sx={{ color: 'info.main' }} />
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {stats?.jobsByStatus?.QUEUED || 0}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Queued
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Daily Cost Trend - Last 30 Days */}
      {stats?.dailyCosts && stats.dailyCosts.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Cost Trend (Last 30 Days)
              </Typography>
              <Chip 
                label={`Total: $${stats.dailyCosts.reduce((sum, day) => sum + day.cost, 0).toFixed(2)}`}
                size="medium"
                sx={{ 
                  backgroundColor: '#e53e3e15',
                  color: '#e53e3e',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              />
            </Box>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={stats.dailyCosts}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis 
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `$${value.toFixed(2)}`}
                  />
                  <RechartsTooltip 
                    formatter={(value: number) => [`$${value.toFixed(2)}`, 'Cost']}
                    labelFormatter={(date: string) => new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="cost" 
                    stroke="#e53e3e" 
                    strokeWidth={2}
                    dot={{ fill: '#e53e3e', r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Daily Successful Runs - Last 30 Days */}
      {stats?.dailyCompletedJobs && stats.dailyCompletedJobs.length > 0 && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" fontWeight={600}>
                Successful Runs (Last 30 Days)
              </Typography>
              <Chip
                label={`Total: ${stats.dailyCompletedJobs.reduce((sum, day) => sum + day.count, 0).toLocaleString()}`}
                size="medium"
                sx={{
                  backgroundColor: '#38a16915',
                  color: '#38a169',
                  fontWeight: 600,
                  fontSize: '0.9rem'
                }}
              />
            </Box>
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.dailyCompletedJobs}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    allowDecimals={false}
                  />
                  <RechartsTooltip
                    formatter={(value: number) => [value.toLocaleString(), 'Successful Runs']}
                    labelFormatter={(date: string) => new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  />
                  <Bar
                    dataKey="count"
                    fill="#38a169"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Top Job Types
              </Typography>
              <Box sx={{ height: 300 }}>
                {jobTypeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={jobTypeData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis dataKey="type" type="category" width={150} tick={{ fontSize: 12 }} />
                      <RechartsTooltip />
                      <Bar dataKey="count" fill="#3182ce" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No job data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Job Status Distribution
              </Typography>
              <Box sx={{ height: 300 }}>
                {statusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={statusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {statusData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <Typography color="text.secondary">No status data available</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Recent Jobs Table */}
      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            Recent Activity (Latest Runs)
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', position: 'relative' }}>
            {tableLoading && (
              <Box sx={{
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(255,255,255,0.6)',
                zIndex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <CircularProgress size={28} />
              </Box>
            )}
            <Table size="small">
              <TableHead sx={{ backgroundColor: 'action.hover' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Processing Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Input Tokens</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Output Tokens</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created At</TableCell>
                  <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>Cost</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats?.recentJobs && stats.recentJobs.length > 0 ? (
                  stats.recentJobs.map((job) => (
                    <React.Fragment key={job.jobId}>
                      <TableRow hover sx={{ '& > *': { borderBottom: expandedRows.has(job.jobId) ? 'none' : undefined } }}>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => toggleRowExpansion(job.jobId)}
                            sx={{ padding: '4px' }}
                          >
                            {expandedRows.has(job.jobId) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {job.userEmail || job.userId.substring(0, 8)}
                        </TableCell>
                        <TableCell>{job.jobType}</TableCell>
                        <TableCell>
                          <Chip 
                            label={job.status} 
                            size="small"
                            color={
                              job.status === 'COMPLETED' ? 'success' : 
                              job.status === 'FAILED' ? 'error' : 
                              job.status === 'PROCESSING' ? 'warning' : 'default'
                            }
                            variant="outlined"
                            sx={{ fontWeight: 500, fontSize: '0.7rem' }}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {job.processingTimeMs ? formatDuration(job.processingTimeMs) : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', textAlign: 'right', fontFamily: 'monospace' }}>
                          {job.inputTokens ? job.inputTokens.toLocaleString() : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', textAlign: 'right', fontFamily: 'monospace' }}>
                          {job.outputTokens ? job.outputTokens.toLocaleString() : '-'}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>
                          {new Date(job.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, textAlign: 'right' }}>
                          {job.costUsd > 0 ? `$${job.costUsd.toFixed(4)}` : '$0.0000'}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                          <Collapse in={expandedRows.has(job.jobId)} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Job ID
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                    {job.jobId}
                                  </Typography>
                                  <Tooltip title="Copy full Job ID" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => copyToClipboard(job.jobId, `Job ID copied: ${job.jobId.substring(0, 12)}...`)}
                                      sx={{ padding: '4px' }}
                                    >
                                      <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    CloudWatch Logs
                                  </Typography>
                                  <Tooltip title="Copy CloudWatch Logs Insights URL for this job" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => copyToClipboard(
                                        getCloudWatchLogsUrl(job.jobId, job.createdAt),
                                        'CloudWatch Logs URL copied'
                                      )}
                                      sx={{ padding: '4px' }}
                                    >
                                      <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    S3 Uploads
                                  </Typography>
                                  <Tooltip title="Copy S3 console URL for this user's uploads" arrow>
                                    <IconButton
                                      size="small"
                                      onClick={() => copyToClipboard(
                                        getS3UploadsConsoleUrl(job.userId),
                                        'S3 uploads path copied'
                                      )}
                                      sx={{ padding: '4px' }}
                                    >
                                      <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    S3 Output
                                  </Typography>
                                  {job.outputLocation ? (
                                    <Tooltip title={`Copy S3 output path: ${job.outputLocation}`} arrow>
                                      <IconButton
                                        size="small"
                                        onClick={() => copyToClipboard(
                                          getS3OutputConsoleUrl(job.outputLocation!),
                                          'S3 output path copied'
                                        )}
                                        sx={{ padding: '4px' }}
                                      >
                                        <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                                      </IconButton>
                                    </Tooltip>
                                  ) : (
                                    <Typography variant="caption" color="text.disabled">-</Typography>
                                  )}
                                </Box>
                                {job.outputLocation && (
                                  <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        OCR Input
                                      </Typography>
                                      <Tooltip title="Copy S3 URL for raw OCR input text" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => copyToClipboard(
                                            getS3OcrInputConsoleUrl(job.outputLocation!),
                                            'OCR input URL copied'
                                          )}
                                          sx={{ padding: '4px' }}
                                        >
                                          <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" color="text.secondary">
                                        AI Response
                                      </Typography>
                                      <Tooltip title="Copy S3 URL for raw AI response text" arrow>
                                        <IconButton
                                          size="small"
                                          onClick={() => copyToClipboard(
                                            getS3AiResponseConsoleUrl(job.outputLocation!),
                                            'AI response URL copied'
                                          )}
                                          sx={{ padding: '4px' }}
                                        >
                                          <CopyIcon sx={{ fontSize: '0.9rem', opacity: 0.6 }} />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </>
                                )}
                              </Box>
                              {job.status === 'FAILED' && job.errorMessage && (
                                <Box sx={{ mt: 2, p: 2, backgroundColor: 'error.light', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
                                  <Typography variant="subtitle2" fontWeight={600} color="error.dark" gutterBottom>
                                    Error Details
                                  </Typography>
                                  <Typography variant="body2" color="error.dark" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                    {job.errorMessage}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                      <Typography color="text.secondary">No recent jobs found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={stats?.totalRecentJobs || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={[10]}
            sx={{ borderTop: 1, borderColor: 'divider' }}
          />
        </CardContent>
      </Card>

      {/* Generated timestamp */}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3, textAlign: 'right' }}>
        Last updated: {stats?.generatedAt ? new Date(stats.generatedAt).toLocaleString() : '-'}
      </Typography>

      {/* Snackbar for copy confirmation */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default DashboardPage;

